/**
 * @file TranscriptScrubber.tsx
 * @brief Fast scroll for a long transcript: a thin handle on the right edge.
 *
 * @details
 * Web twin of the phone's TranscriptScrubber (FRONTEND MeetingDetails/
 * components/TranscriptScrubber.tsx, DES 29-09-2026: "transcript per koi quick
 * scroll lagsakta hai"); added to web 05-10-2026 from the parity check.
 *
 * The handle maps to a LINE (turn index), not to pixels: turns vary wildly in
 * height, and a pixel map would spend most of the track on a few long turns.
 * Drag it and a bubble shows that line's time and speaker; let go and the page
 * jumps to it in one move. While idle the handle follows the scroll, showing
 * which line is at the top of the reading area.
 *
 * The page scrolls inside the shell's `<main data-scroll-root>`, not the
 * window, so positions are read and set against that element.
 *
 * @references
 * - shared/scroll/scrollMainTo.ts (getMainScrollRoot, getMainAlignLineY)
 * - MeetingDetailView.tsx (TranscriptTab mounts it beside the turn list)
 *
 * @author DES
 * @date 05-10-2026
 */

import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { getMainAlignLineY, getMainScrollRoot } from "@/shared/scroll/scrollMainTo";
import { cn } from "@/shared/theme/cn";

/** Below this many turns the page scrolls fine on its own. */
export const TRANSCRIPT_SCRUBBER_MIN_TURNS = 30;
const MIN_TURNS = TRANSCRIPT_SCRUBBER_MIN_TURNS;
const THUMB_H = 36;

export function TranscriptScrubber({
  listRef,
  turns,
}: {
  /** The `<ol>` whose children are the turns, in order. */
  listRef: RefObject<HTMLOListElement | null>;
  turns: { speaker: string; time: string | null }[];
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const dragging = dragIndex !== null;
  const count = turns.length;

  // Follow the scroll: the first turn whose bottom is below the reading line.
  useEffect(() => {
    const root = getMainScrollRoot();
    if (!root || count < MIN_TURNS) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const items = listRef.current?.children;
        if (!items?.length) return;
        const line = getMainAlignLineY();
        let found = items.length - 1;
        for (let i = 0; i < items.length; i += 1) {
          if ((items[i] as HTMLElement).getBoundingClientRect().bottom > line) {
            found = i;
            break;
          }
        }
        setIndex(found);
      });
    };
    onScroll();
    root.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      root.removeEventListener("scroll", onScroll);
    };
  }, [listRef, count]);

  const indexAt = useCallback(
    (clientY: number) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect || count < 2) return 0;
      const usable = Math.max(1, rect.height - THUMB_H);
      const ratio = Math.min(1, Math.max(0, (clientY - rect.top - THUMB_H / 2) / usable));
      return Math.round(ratio * (count - 1));
    },
    [count],
  );

  const jumpTo = useCallback(
    (i: number) => {
      const root = getMainScrollRoot();
      const el = listRef.current?.children[i] as HTMLElement | undefined;
      if (!root || !el) return;
      const delta = el.getBoundingClientRect().top - getMainAlignLineY();
      root.scrollTo({ top: root.scrollTop + delta, behavior: "auto" });
      setIndex(i);
    },
    [listRef],
  );

  if (count < MIN_TURNS) return null;

  const shown = dragIndex ?? index;
  const ratio = count > 1 ? shown / (count - 1) : 0;
  const turn = turns[shown];

  return (
    <div
      ref={trackRef}
      role="scrollbar"
      aria-orientation="vertical"
      aria-label="Jump through the transcript"
      aria-valuemin={1}
      aria-valuemax={count}
      aria-valuenow={shown + 1}
      tabIndex={0}
      onKeyDown={(e) => {
        const step = e.key === "PageDown" ? 10 : e.key === "PageUp" ? -10 : e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
        if (!step) return;
        e.preventDefault();
        jumpTo(Math.min(count - 1, Math.max(0, index + step)));
      }}
      onPointerDown={(e) => {
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragIndex(indexAt(e.clientY));
      }}
      onPointerMove={(e) => {
        if (dragging) setDragIndex(indexAt(e.clientY));
      }}
      onPointerUp={(e) => {
        if (!dragging) return;
        const i = indexAt(e.clientY);
        setDragIndex(null);
        jumpTo(i);
      }}
      onPointerCancel={() => setDragIndex(null)}
      className="group fixed bottom-36 right-4 top-36 z-30 w-6 cursor-pointer touch-none select-none focus-visible:outline-none"
    >
      {/* The rail: barely there until you reach for it. */}
      <div className="absolute inset-y-0 left-1/2 w-1 -translate-x-1/2 rounded-full bg-black/[0.06] transition-colors group-hover:bg-black/[0.1] dark:bg-white/[0.08] dark:group-hover:bg-white/[0.14]" />
      <div
        className={cn(
          "absolute left-1/2 w-1.5 -translate-x-1/2 rounded-full transition-colors",
          dragging ? "bg-brand" : "bg-foreground/35 group-hover:bg-foreground/55",
        )}
        style={{ height: THUMB_H, top: `calc(${ratio} * (100% - ${THUMB_H}px))` }}
      />
      {dragging && turn ? (
        <div
          className="pointer-events-none absolute right-7 -translate-y-1/2 whitespace-nowrap rounded-full bg-foreground px-3 py-1.5 text-xs font-semibold text-background shadow-lg"
          style={{ top: `calc(${ratio} * (100% - ${THUMB_H}px) + ${THUMB_H / 2}px)` }}
        >
          {turn.time ? <span className="tabular-nums">{turn.time}</span> : null}
          {turn.time ? <span className="opacity-60">{"  ·  "}</span> : null}
          {turn.speaker}
        </div>
      ) : null}
    </div>
  );
}
